import React, { useState, useEffect } from 'react';
import { useDispatchRequest, useQuery } from '@redux-requests/react';
import { Divider, Link, Button, CircularProgress, Accordion, AccordionSummary } from '@mui/material';
import { Form, Formik, Field, FieldProps } from 'formik';
import { Info } from '@globalvisioninc/assets/icons';
import { GVTypography } from 'components/lib';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVSelect from 'components/lib/GVSelect/GVSelect';
import { SELECT_EMPTY_VALUE } from '@globalvisioninc/ui/select';
import { Separator } from '@globalvisioninc/ui/separator';
import GVSwitch from 'components/lib/GVSwitch/GVSwitch';
import { createConnection, fetchActiveUserOrganization, updateConnection } from 'store/myAccount/requests';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { SSOConnectionFormData } from 'store/myAccount/types';
import MyAccountCard from '../Card';
import UploadCertificate from './UploadCertificate';
import LinkedTextField from './LinkedTextField';
import { CopyButton } from './CopyButton';
import styles from './Security.module.scss';

const ALGORITHM_OPTIONS = [
  { label: '', value: SELECT_EMPTY_VALUE },
  { label: 'RSA-SHA256', value: 'rsa-sha256' },
  { label: 'RSA-SHA1', value: 'rsa-sha1' },
];

const ALGORITHM_DIGEST_OPTIONS = [
  { label: '', value: SELECT_EMPTY_VALUE },
  { label: 'SHA256', value: 'sha256' },
  { label: 'SHA1', value: 'sha1' },
];

const PROTOCOL_BINDING_OPTIONS = [
  { label: '', value: SELECT_EMPTY_VALUE },
  { label: 'HTTP-POST', value: 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST' },
  { label: 'HTTP-Redirect', value: 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect' },
];

type SSOFormErrorType = Partial<{
  [key in keyof SSOConnectionFormData]: string;
}>;

const SAML_HELP_LINK = 'https://help.globalvision.co/verify/verify-administration-guide';

const Security = ({ enableSSOConnection }: { enableSSOConnection: () => void }) => {
  const dispatchRequest = useDispatchRequest();
  const { data: organizationData } = useQuery({ type: fetchActiveUserOrganization });
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [currentDuplicateName, setCurrentDuplicateName] = useState<string | null>(null);
  const existingConnection = organizationData?.connection;
  const connectionEnabled = existingConnection?.enabled;
  const formikRef = React.useRef<any>(null);

  useEffect(() => {
    if (currentDuplicateName) {
      formikRef.current?.validateForm();
    }
  }, [currentDuplicateName]);

  const initialValues: SSOConnectionFormData = {
    connectionName: existingConnection?.connectionName || '',
    signInURL: existingConnection?.signInURL || '',
    signOutURL: existingConnection?.signOutURL || '',
    emailMapping: existingConnection?.fieldsMap?.email || '',
    certificate: null,
    signRequestAlgorithm: existingConnection?.signRequestAlgorithm,
    signRequestAlgorithmDigest: existingConnection?.signRequestAlgorithmDigest,
    protocolBinding: existingConnection?.protocolBinding,
    enabled: !!connectionEnabled,
  };

  const validateForm = (values: SSOConnectionFormData): SSOFormErrorType => {
    const urlRegex =
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
    const connectionNameRegex = /^[a-zA-Z0-9](-[a-zA-Z0-9]|[a-zA-Z0-9])*$/;

    const errors: SSOFormErrorType = {};

    if (!values.connectionName.trim()) {
      errors.connectionName = 'Required field';
    } else if (!connectionNameRegex.test(values.connectionName) || values.connectionName.length > 128) {
      errors.connectionName =
        "Must start and end with an alphanumeric character and can only contain alphanumeric characters and '-'. Max length 128";
    } else if (currentDuplicateName && values.connectionName.toLowerCase() === currentDuplicateName.toLowerCase()) {
      errors.connectionName = 'There is already a connection name with this name. Choose a different one.';
    }

    if (!values.signInURL.trim()) {
      errors.signInURL = 'Required field';
    } else if (!urlRegex.test(values.signInURL)) {
      errors.signInURL = 'Must be a valid URL';
    }

    if (values.signOutURL && !urlRegex.test(values.signOutURL)) {
      errors.signOutURL = 'Must be a valid URL';
    }

    if (!existingConnection && !values.certificate) {
      // if creating the connection, cert is required
      errors.certificate = 'Required certificate';
    }

    if (!values.emailMapping.trim()) {
      errors.emailMapping = 'Required Field';
    }

    return errors;
  };

  const handleSubmitForm = (ssoData: SSOConnectionFormData) => {
    setCurrentDuplicateName(null);
    setSavingConnection(true);
    if (existingConnection) {
      const updateFormData = new FormData();
      Object.entries(ssoData).forEach((entry) => {
        if (entry[0] === 'connectionName' || entry[1] || entry[1] === false) {
          // we don't pass if the field value is empty
          updateFormData.append(entry[0], entry[1]);
        }
      });
      dispatchRequest(
        updateConnection(existingConnection.connectionId, updateFormData, existingConnection.enabled),
      ).then((resp) => {
        if (resp.data) {
          // if connection was previously disabled and updated to be enabled, navigate tabs and update ui
          if (!connectionEnabled && resp.data?.enabled) {
            enableSSOConnection();
          }
        } else if (resp.error) {
          if (resp.error.message?.includes('409')) {
            setCurrentDuplicateName(ssoData.connectionName);
          }
        }
        setSavingConnection(false);
      });
      return;
    }

    // create new connection
    const createFormData = new FormData();
    Object.entries(ssoData).forEach((entry) => {
      if (entry[1] || entry[1] === false) {
        // we don't pass if the field value is empty
        createFormData.append(entry[0], entry[1]);
      }
    });
    dispatchRequest(createConnection(createFormData)).then((resp) => {
      if (resp.data) {
        if (resp.data?.enabled) {
          // if connection was created and enabled, we navigate tabs and update ui
          enableSSOConnection();
        }
        return;
      }
      if (resp.error) {
        if (resp.error.message?.includes('409')) {
          setCurrentDuplicateName(ssoData.connectionName);
        }
      }
      setSavingConnection(false);
    });
  };

  return (
    <MyAccountCard title={<GVTypography variant="h6">Single Sign-On</GVTypography>}>
      <div className={styles.root}>
        <Divider light />
        <div className={styles.info}>
          <GVTypography variant="subtitle1">
            {'Learn more about SAML SSO configuration '}
            <Link href={SAML_HELP_LINK} underline="always" target="_blank" rel="noopener noreferrer">
              here
            </Link>
          </GVTypography>
          <Formik
            key="myTeamForm"
            initialValues={initialValues}
            enableReinitialize
            validate={validateForm}
            onSubmit={(ssoData) => {
              handleSubmitForm(ssoData);
            }}
            innerRef={formikRef}
          >
            {(formikProps) => (
              <Form onSubmit={formikProps.handleSubmit}>
                <div className={styles.inputContainer}>
                  <Field name="connectionName">
                    {(fieldProps: FieldProps) => (
                      <GVTextField
                        className={styles.connectionName}
                        name={fieldProps.field.name}
                        onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                        value={fieldProps.field.value}
                        label="Connection name"
                        id="connection-name-input"
                        isDisabled={savingConnection || !!existingConnection}
                        hasError={!!fieldProps.meta.error}
                        message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : null}
                      />
                    )}
                  </Field>
                  <GVTypography className={styles.note}>
                    This is a logical identifier of the connection, and it cannot be changed.
                  </GVTypography>
                  <Field name="signInURL">
                    {(fieldProps: FieldProps) => (
                      <GVTextField
                        name={fieldProps.field.name}
                        onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                        value={fieldProps.field.value}
                        label="Sign-in page URL"
                        id="sign-in-url-input"
                        isDisabled={savingConnection}
                        hasError={!!fieldProps.meta.error}
                        message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      />
                    )}
                  </Field>
                  <LinkedTextField
                    name="signOutURL"
                    label="Sign-out page URL"
                    linkedfieldvalue={formikProps.values.signInURL}
                    isDisabled={savingConnection}
                  />
                  <Field name="emailMapping">
                    {(fieldProps: FieldProps) => (
                      <GVTextField
                        name={fieldProps.field.name}
                        onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                        value={fieldProps.field.value}
                        label="Email Mapping"
                        id="email-mapping-input"
                        isDisabled={savingConnection}
                        elementEnd={
                          <GVTooltip
                            className={styles.tooltip}
                            title={
                              <GVTypography variant="inherit">
                                Please enter the field name used to store your users&apos; email address by your IDP/SSO
                                provider.
                              </GVTypography>
                            }
                          >
                            <Info className={styles.icon} />
                          </GVTooltip>
                        }
                        hasError={!!fieldProps.meta.error}
                        message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      />
                    )}
                  </Field>
                </div>
                <div className={styles.upload}>
                  <UploadCertificate name="certificate" disabled={savingConnection} />
                </div>
                <Accordion
                  className={styles.expansionPanel}
                  expanded={isAccordionExpanded}
                  onChange={() => setIsAccordionExpanded(!isAccordionExpanded)}
                >
                  <AccordionSummary
                    id="AdvancedSettings"
                    className={styles.accordionSummary}
                    expandIcon={
                      <ExpandMoreIcon
                        color="primary"
                        id="AdvancedSettingsDropDown"
                        data-testid="advanced_settings_drop_down"
                      />
                    }
                  >
                    <GVTypography className={styles.subtitle} emphasis="superhigh" variant="subtitle1">
                      Advanced Settings
                    </GVTypography>
                  </AccordionSummary>
                  <Field name="signRequestAlgorithm">
                    {(fieldProps: FieldProps) => (
                      <GVSelect
                        options={ALGORITHM_OPTIONS}
                        name={fieldProps.field.name}
                        onValueChange={(nextValue) =>
                          fieldProps.form.setFieldValue(
                            fieldProps.field.name,
                            nextValue === SELECT_EMPTY_VALUE ? '' : nextValue,
                          )
                        }
                        value={fieldProps.field.value === '' ? SELECT_EMPTY_VALUE : fieldProps.field.value}
                        label="Sign Request Algorithm"
                        id="sign-request-algorithm-input"
                        isDisabled={savingConnection}
                      />
                    )}
                  </Field>
                  <Field name="signRequestAlgorithmDigest">
                    {(fieldProps: FieldProps) => (
                      <GVSelect
                        options={ALGORITHM_DIGEST_OPTIONS}
                        name={fieldProps.field.name}
                        onValueChange={(nextValue) =>
                          fieldProps.form.setFieldValue(
                            fieldProps.field.name,
                            nextValue === SELECT_EMPTY_VALUE ? '' : nextValue,
                          )
                        }
                        value={fieldProps.field.value === '' ? SELECT_EMPTY_VALUE : fieldProps.field.value}
                        label="Sign Request Algorithm Digest"
                        id="sign-request-algorithm-digest-input"
                        isDisabled={savingConnection}
                      />
                    )}
                  </Field>
                  <Field name="protocolBinding">
                    {(fieldProps: FieldProps) => (
                      <GVSelect
                        options={PROTOCOL_BINDING_OPTIONS}
                        name={fieldProps.field.name}
                        onValueChange={(nextValue) =>
                          fieldProps.form.setFieldValue(
                            fieldProps.field.name,
                            nextValue === SELECT_EMPTY_VALUE ? '' : nextValue,
                          )
                        }
                        value={fieldProps.field.value === '' ? SELECT_EMPTY_VALUE : fieldProps.field.value}
                        label="Protocol Binding"
                        id="protocol-binding-input"
                        isDisabled={savingConnection}
                      />
                    )}
                  </Field>
                  <Separator.Root />
                </Accordion>
                <Divider light />
                <GVTypography className={styles.subtitle} emphasis="superhigh" variant="subtitle1">
                  Configuration Settings
                </GVTypography>
                <div className={styles.inputContainer}>
                  <GVTextField
                    label="Entity ID"
                    isLabelDynamic={false}
                    isReadOnly
                    value={
                      formikProps.values.connectionName
                        ? `urn:auth0:${import.meta.env.VITE_AUTH0_DOMAIN?.split('.')[0]}:${formikProps.values.connectionName}`
                        : ''
                    }
                    id="entity-id-input"
                    className={styles.field}
                  >
                    <CopyButton
                      text={
                        formikProps.values.connectionName
                          ? `urn:auth0:${import.meta.env.VITE_AUTH0_DOMAIN?.split('.')[0]}:${formikProps.values.connectionName}`
                          : ''
                      }
                      label="Entity ID"
                    />
                  </GVTextField>
                  <GVTextField
                    label="Reply URL"
                    isLabelDynamic={false}
                    isReadOnly
                    value={
                      formikProps.values.connectionName
                        ? `https://${import.meta.env.VITE_AUTH0_DOMAIN}/login/callback?connection=${formikProps.values.connectionName}`
                        : ''
                    }
                    id="reply-url-input"
                    className={styles.field}
                  >
                    <CopyButton
                      text={
                        formikProps.values.connectionName
                          ? `https://${import.meta.env.VITE_AUTH0_DOMAIN}/login/callback?connection=${formikProps.values.connectionName}`
                          : ''
                      }
                      label="Reply URL"
                    />
                  </GVTextField>
                  <GVTextField
                    label="Domain"
                    isLabelDynamic={false}
                    isReadOnly
                    className={styles.field}
                    value={
                      typeof existingConnection?.configurationSettings.domain === 'string'
                        ? existingConnection.configurationSettings.domain
                        : typeof organizationData?.domain === 'string'
                          ? organizationData.domain
                          : ''
                    }
                    id="domain-input"
                  />
                  <GVTextField
                    label="ACS URL (Assertion Consumer Service URL)"
                    isLabelDynamic={false}
                    isReadOnly
                    value={
                      formikProps.values.connectionName
                        ? `https://${import.meta.env.VITE_AUTH0_DOMAIN}/login/callback?connection=${formikProps.values.connectionName}`
                        : ''
                    }
                    id="ACS-url-id"
                    className={styles.field}
                  >
                    <CopyButton
                      text={
                        formikProps.values.connectionName
                          ? `https://${import.meta.env.VITE_AUTH0_DOMAIN}/login/callback?connection=${formikProps.values.connectionName}`
                          : ''
                      }
                      label="ACS URL"
                    />
                  </GVTextField>
                  <GVTextField
                    label="SLO Endpoint (Single Logout URL)"
                    isLabelDynamic={false}
                    isReadOnly
                    value={`https://${import.meta.env.VITE_AUTH0_DOMAIN}/logout`}
                    id="SLO-Endpoint-id"
                    className={styles.field}
                  >
                    <CopyButton text={`https://${import.meta.env.VITE_AUTH0_DOMAIN}/logout`} label="SLO Endpoint" />
                  </GVTextField>
                  <GVTextField
                    label="SLO Binding"
                    isLabelDynamic={false}
                    isReadOnly
                    className={styles.field}
                    value="HTTP-Post"
                    id="SLO-Binding-input"
                  />
                  <GVTextField
                    label="Subject NameId Format"
                    isLabelDynamic={false}
                    isReadOnly
                    className={styles.field}
                    value="Unspecified"
                    id="NameId-Input"
                  />
                </div>
                <Divider light />
                <Divider light />
                <div className={styles.footer}>
                  <div className={styles.switch}>
                    <Field name="enabled">
                      {(fieldProps: FieldProps) => {
                        return (
                          <GVSwitch
                            name={fieldProps.field.name}
                            onChange={fieldProps.field.onChange}
                            value={fieldProps.field.value}
                            checked={fieldProps.field.value}
                            disabled={
                              savingConnection || existingConnection?.enabled // connection data exists, and the connection is enabled
                            }
                          />
                        );
                      }}
                    </Field>
                    <GVTypography
                      variant="subtitle1"
                      display="inline"
                      emphasis={existingConnection?.enabled ? 'disabled' : 'high'}
                    >
                      {formikProps.values.enabled ? 'Enabled' : 'Disabled'}
                    </GVTypography>
                  </div>
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={savingConnection || !formikProps.isValid || !formikProps.dirty}
                    id="user-save-button"
                    data-testid="user_save_changes"
                  >
                    Save changes
                    {savingConnection && <CircularProgress className={styles.saving} size={16} color="primary" />}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </MyAccountCard>
  );
};

export default Security;
